import { useMemo } from 'react';
import { computeLineDiff, diffStats } from './lineDiff';

interface UnifiedDiffProps {
  before: string;
  after: string;
}

export function UnifiedDiff({ before, after }: UnifiedDiffProps) {
  const lines = useMemo(() => computeLineDiff(before, after), [before, after]);
  const stats = useMemo(() => diffStats(lines), [lines]);

  if (before === after) {
    return (
      <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        No changes between draft and currently published version.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded border border-gray-200">
      <div className="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-600">
        <span className="text-green-700">+{stats.added}</span>
        <span className="mx-2 text-gray-400">·</span>
        <span className="text-red-700">−{stats.removed}</span>
      </div>
      <pre className="max-h-[480px] overflow-auto bg-white font-mono text-xs leading-5">
        {lines.map((line, idx) => {
          const bg =
            line.kind === 'added'
              ? 'bg-green-50 text-green-900'
              : line.kind === 'removed'
                ? 'bg-red-50 text-red-900'
                : 'bg-white text-gray-700';
          const prefix = line.kind === 'added' ? '+' : line.kind === 'removed' ? '−' : ' ';
          return (
            <div
              key={idx}
              className={`whitespace-pre-wrap break-all px-3 ${bg}`}
            >
              <span className="mr-2 select-none text-gray-400">{prefix}</span>
              {line.text || ' '}
            </div>
          );
        })}
      </pre>
    </div>
  );
}
